import { BetActionErrorResponseDataDto } from 'api/common.generated';
import { GetCategoriesIcon, GetSportIcon } from 'assets/icons';
import CloseIconRounded from 'assets/images/actions/closeIconRounded.svg?react';
import ThreeDotsIcon from 'assets/images/ThreeDotsIcon.svg?react';
import cn from 'classnames';
import BetslipCouponWarning from 'features/coupon/component/BetslipCouponWarning';
import SystemBetGroupingPopup from 'features/coupon/component/SystemBetGroupingPopup';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useClickOutside from 'shared/hooks/useClickOutside';
import { PlaceBet } from 'shared/interface';

import { ActionType } from 'api/betslipAnalytics.api';
import { BetBuilderOddsArray } from 'features/coupon/interface';
import { EsSportEventData } from 'features/esSport/interface/interface';
import { LiveMainState } from 'features/live/interface/live';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import RenderConditionalLink from 'shared/components/ConditionalLink';
import LiveAnimatedDot from 'shared/components/liveAnimatedDot';
import { useBetslipAnalytics } from 'shared/hooks/useBetslipAnalytics';
import { State } from 'shared/interface';
import { OUTCOME_NAME_DRAW } from 'shared/util/utility';
import { gameTypeIsBetBuilder } from '../../../../shared/components/outcomeCards/gameTypeIds';
import { getRedirectionPathFromBetslip } from '../../utility';
import './styles.scss';

interface BetslipCouponCardProps {
	bet: PlaceBet;
	removeBet: (id: string) => void;
	handelSelectedBet: (value: boolean, id: string) => void;
	numOfItemsInBetslip: number;
	couponType: string;
	warnings: string[];
	completedEvents: string[];
	changeGroup?: (outcomeId: string, newGroupIndex: number) => void;
	getSystemBetGroupName?: (outcomeId: string) => string | undefined;
	getNumOfGroups?: (outcomeId: string) => number;
	handleIsRestoringBets: (isRestoring: boolean) => void;
	betActionErrors: BetActionErrorResponseDataDto[];
	isBetBuilder?: boolean;
	isBetSlip?: boolean | undefined;
	selectedOddsArray?: BetBuilderOddsArray[];
	totalOdds?: number;
}

const BetslipCouponCard = (props: BetslipCouponCardProps) => {
	const {
		bet,
		removeBet,
		numOfItemsInBetslip,
		couponType,
		handelSelectedBet,
		changeGroup = () => {},
		getSystemBetGroupName = () => '',
		getNumOfGroups = () => 0,
		handleIsRestoringBets,
		betActionErrors,
		warnings,
		isBetSlip,
		selectedOddsArray,
		isBetBuilder = false,
		completedEvents,
	} = props;

	const [localBet, setLocalBet] = useState<PlaceBet>(bet);
	const [localIsBetBuilder, setLocalIsBetBuilder] = useState<boolean>(isBetBuilder);
	const [localSelectedOddsArray, setLocalSelectedOddsArray] = useState<BetBuilderOddsArray[]>(selectedOddsArray ?? []);

	const isBetBuilderBet = (bet: PlaceBet): boolean =>
		!!bet.gameType &&
		gameTypeIsBetBuilder(typeof bet.gameType === 'string' ? parseInt(bet.gameType) : bet.gameType) &&
		!!bet.gameName?.includes(' | ') &&
		!!(bet.outcomeName ?? '').includes(' | ');

	useEffect(() => {
		if (isBetBuilderBet(bet)) {
			const localBetCopy = { ...bet };

			const gameNameArray = localBetCopy.gameName?.split(' | ');
			const outcomeNameArray = localBetCopy.outcomeName?.split(' | ');
			const localBetBuilderOddsArray: BetBuilderOddsArray[] = [];
			gameNameArray?.forEach((gameName, index) => {
				localBetBuilderOddsArray.push({
					gameName: gameName,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					name: outcomeNameArray ? outcomeNameArray[index]! : '',
				});
			});

			localBetCopy.betBuilderOddsArray = localBetBuilderOddsArray;
			localBetCopy.isBetBuilder = true;

			setLocalBet(localBetCopy);
			setLocalIsBetBuilder(true);
			setLocalSelectedOddsArray(localBetBuilderOddsArray);
		} else if (isBetBuilder) {
			setLocalBet(bet);
			setLocalIsBetBuilder(isBetBuilder);
			setLocalSelectedOddsArray(selectedOddsArray ?? []);
		} else {
			setLocalBet(bet);
			setLocalIsBetBuilder(false);
			setLocalSelectedOddsArray([]);
		}
	}, [bet, isBetBuilder]);

	const { t } = useTranslation();
	const { addAction } = useBetslipAnalytics();
	const history = useHistory();
	const { categoryId, sportId } = localBet;
	const eventName = localBet.eventName.replaceAll('(live)', '');
	const fetchAllLiveDetails = useSelector<State, LiveMainState[]>((state) => state.live.allLiveEvents);
	const popularLiveBettingData = useSelector<State, EsSportEventData[]>((state) => state.home.popularLiveBettingData);

	const [showGroupingDialog, setShowGroupingDialog] = useState(false);

	const dialogRef = useClickOutside(() => setShowGroupingDialog(false), 'click');

	const redirectToEventPage = localBet ? getRedirectionPathFromBetslip(localBet) : '';

	const betslipAnalyticsObject = {
		banker: localBet.banker,
		categoryId: localBet.categoryId,
		eventId: localBet.eventId,
		eventName: localBet.eventName,
		eventType: localBet.eventType,
		gameId: localBet.gameId,
		gameName: localBet.gameName,
		gameType: localBet.gameType,
		isLive: localBet.isLive,
		league: localBet.league,
		outcomeId: localBet.outcomeId,
		outcomeOdds: localBet.outcomeOdds,
		outcomeUpdateState: localBet.outcomeUpdateState,
		selectedBet: localBet.selectedBet,
		sportId: localBet.sportId,
	};

	const handleRemoveBet = () => {
		removeBet(localBet.outcomeId);
		handleIsRestoringBets(false);
		addAction(betslipAnalyticsObject, `${history.location.pathname}/${history.location.hash}`, ActionType.REMOVE);
	};

	if (!eventName) {
		return null;
	}

	const getCurrentEventFromLiveData = (allLiveData: any, eventId: number) => {
		if (!allLiveData) {
			return null;
		}

		const sportCategory = allLiveData.find((category: any) => category.events?.some((event: any) => event.eventId === eventId));

		if (!sportCategory) {
			return null;
		}
		const currentEvent = sportCategory.events.find((event: any) => event.eventId === eventId);

		return currentEvent.parentId || null;
	};

	const parentIdFromLive = getCurrentEventFromLiveData(fetchAllLiveDetails, localBet.eventId);

	const parentIdFromPopular = popularLiveBettingData.find((el) => {
		return el.eventId === localBet.eventId;
	});

	const isEventCompleted = () => {
		return completedEvents.includes(localBet.outcomeId.toString());
	};

	const isBetUnavailable = localBet.isCompleted;

	return (
		<div
			className={cn('betslipCouponCard', {
				'betslipCouponCard--ako': couponType === 'ako',
				'betslipCouponCard--system': couponType === 'system',
				'betslipCouponCard--unavailable': isBetUnavailable || isEventCompleted(),
			})}
		>
			<div className="betslipCouponCard__wrapper">
				<div className="betslipCouponCard__name">
					<Link className="betslipCouponCard__name__info" to={getRedirectionPathFromBetslip(localBet)}>
						<div className="betslipCouponCard__name__icons">
							{localBet.isLive && <LiveAnimatedDot />}
							<GetSportIcon id={sportId} />

							<GetCategoriesIcon
								category={{
									sportId: sportId as number,
									parentCategory: (parentIdFromLive as number) || (parentIdFromPopular?.parentId as number),
									categoryId: categoryId as number,
								}}
							/>
						</div>
						<div
							className={cn('betslipCouponCard__name__text', {
								'betslipCouponCard__name__text--truncate': eventName === localBet.gameName,
							})}
						>
							{eventName}
						</div>
					</Link>

					<div className="betslipCouponCard__name__remove" data-cy="betslip-common">
						{couponType === 'ako' ? (
							<CloseIconRounded onClick={handleRemoveBet} />
						) : (
							<ThreeDotsIcon onClick={() => setShowGroupingDialog(true)} />
						)}
					</div>
				</div>
				<div className="betslipCouponCard__odds">
					{!localIsBetBuilder ? (
						<Link to={getRedirectionPathFromBetslip(localBet)}>
							<div className="betslipCouponCard__odds__row">{localBet.gameName}</div>
							<div className="betslipCouponCard__odds__row betslipCouponCard__odds__row--bottom">
								<div className="">{localBet?.outcomeName === OUTCOME_NAME_DRAW ? t('draw') : localBet.outcomeName}</div>
								<div className="">{localBet?.outcomeOdds?.toFixed(2)}</div>
							</div>
						</Link>
					) : (
						<RenderConditionalLink to={redirectToEventPage} linkCondition={!!isBetSlip}>
							{localSelectedOddsArray &&
								localSelectedOddsArray.map((item, index) => (
									<div className="betslipCouponCard__odds__item" key={`selectedOddsArray-${index}`}>
										<div className="betslipCouponCard__odds__text">
											<p className="betslipCouponCard__odds__text__game">{item.gameName}</p>
											<p className="betslipCouponCard__odds__text__outcome">{item.name}</p>
										</div>
										{props.totalOdds && index + 1 === localSelectedOddsArray.length && (
											<span className="bet-odds">{props.totalOdds.toFixed(2)}</span>
										)}
									</div>
								))}
						</RenderConditionalLink>
					)}
				</div>
			</div>
			<BetslipCouponWarning
				isBetUnavailable={isBetUnavailable}
				isEventCompleted={isEventCompleted}
				bet={localBet}
				warnings={warnings}
				betActionErrors={betActionErrors}
			/>

			{showGroupingDialog && (
				<SystemBetGroupingPopup
					showGroupingDialog={showGroupingDialog}
					setShowGroupingDialog={() => setShowGroupingDialog(false)}
					dialogRef={dialogRef}
					removeBet={removeBet}
					groupArray={[...Array(Number(getNumOfGroups(localBet.outcomeId)))]}
					numOfItemsInBetslip={numOfItemsInBetslip}
					outcomeId={localBet.outcomeId}
					isBanker={localBet.banker}
					handleGroupChange={changeGroup}
					handleSelectedBet={handelSelectedBet}
					getSystemBetGroupName={getSystemBetGroupName}
				/>
			)}
		</div>
	);
};

export default BetslipCouponCard;
